import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Divider,
  Chip,
} from '@mui/material'
import {
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'

const DGRDetail = ({ report, onEdit, onClose }) => {
  if (!report) {
    return (
      <Typography color="error">
        Report details not available
      </Typography>
    )
  }

  const getPGColor = (pg) => {
    switch (pg) {
      case 'Pass':
        return 'success'
      case 'Warning':
        return 'warning'
      case 'Fail':
        return 'error'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        pb={2}
        borderBottom="1px solid #e0e0e0"
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Daily Generation Report
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {report.plantName} - {dayjs(report.date || report.reportDate).isValid() ? dayjs(report.date || report.reportDate).format('DD-MM-YYYY') : 'N/A'}
          </Typography>
        </Box>
        <Box>
          <Button
            startIcon={<EditIcon />}
            onClick={onEdit}
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            variant="text"
            size="small"
          >
            Close
          </Button>
        </Box>
      </Box>

      {/* Plant Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Plant Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Plant Name
              </Typography>
              <Typography variant="body1">{report.plantName}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Plant Location
              </Typography>
              <Typography variant="body1">
                {report.plantLocation || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Plant Capacity
              </Typography>
              <Typography variant="body1">
                {report.plantCapacity} kWp
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Report Date
              </Typography>
              <Typography variant="body1">
                {dayjs(report.date || report.reportDate).isValid() ? dayjs(report.date || report.reportDate).format('DD-MM-YYYY') : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Generation Data */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Generation Data
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {report.sensor !== false && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  GTI (W/m²)
                </Typography>
                <Typography variant="body1">
                  {report.gti !== null && report.gti !== undefined && isFinite(Number(report.gti))
                    ? Number(report.gti).toFixed(2)
                    : 'N/A'}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Actual Yield (kWh/kWp)
              </Typography>
              <Typography variant="body1">
                {report.actYield !== null && report.actYield !== undefined && isFinite(Number(report.actYield))
                  ? Number(report.actYield).toFixed(2)
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Actual Generation (kWh)
              </Typography>
              <Typography variant="body1">
                {report.actGeneration !== null && report.actGeneration !== undefined && isFinite(Number(report.actGeneration))
                  ? Number(report.actGeneration).toFixed(2)
                  : 'N/A'}
              </Typography>
            </Grid>
            {report.sensor !== false && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  PR (Performance Ratio %)
                </Typography>
                  <Typography variant="body1">
                    {report.pr !== null && report.pr !== undefined && isFinite(Number(report.pr))
                      ? `${Number(report.pr).toFixed(2)}%`
                      : 'N/A'}
                  </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Remarks */}
      {report.remarks && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Remarks
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2">{report.remarks}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <Box mt={3} pt={2} borderTop="1px solid #e0e0e0">
        <Typography variant="caption" color="textSecondary">
          Created on: {report.createdAt ? (dayjs(report.createdAt).isValid() ? dayjs(report.createdAt).format('DD-MM-YYYY HH:mm') : 'N/A') : 'N/A'}
        </Typography>
      </Box>
    </Box>
  )
}

export default DGRDetail
